import { colors } from "@components/ui/colors";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { usePaymentMethodState } from "../PaymentMethods/states/usePaymentMethod";
import type { InvoiceAdjustmentKind } from "../shared/types";
import type { InvoiceServiceFeeDraft } from "./InvoiceServiceFeesCard";

export const CC_FEE_ID = "credit-card-processing-fee";
const DEFAULT_CC_RATE = "3";

interface PaymentProcessingFeeCardProps {
  serviceFees: InvoiceServiceFeeDraft[];
  onServiceFeesChange: React.Dispatch<React.SetStateAction<InvoiceServiceFeeDraft[]>>;
  computedFeeAmount?: number;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

const PRESET_PERCENTAGES = [
  { label: "2.9%", value: "2.9", tag: "Stripe base", tooltip: "Stripe standard fee: 2.9% + $0.30 per charge" },
  { label: "3.0%", value: "3", tag: "Recommended", tooltip: "Standard average to offset card processing fees" },
  { label: "3.5%", value: "3.5", tag: "PayPal / Full", tooltip: "Covers PayPal payments (up to 3.49% + $0.49) & fixed costs" },
  { label: "4.0%", value: "4", tag: "Buffer", tooltip: "Full coverage for international or high-reward cards" },
];

export function PaymentProcessingFeeCard({ serviceFees, onServiceFeesChange, computedFeeAmount = 0 }: PaymentProcessingFeeCardProps) {
  const { markAsPaid } = usePaymentMethodState();

  const existingCcFee = useMemo(() => {
    return serviceFees.find((f) => f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee");
  }, [serviceFees]);

  const isEnabled = Boolean(existingCcFee);
  const [feeKind, setFeeKind] = useState<InvoiceAdjustmentKind>(existingCcFee?.kind ?? "percentage");
  const [feeValue, setFeeValue] = useState<string>(existingCcFee?.value ?? DEFAULT_CC_RATE);
  const [feeLabel, setFeeLabel] = useState<string>(existingCcFee?.label ?? "Credit card fee");
  const [showRateGuide, setShowRateGuide] = useState(false);

  // Keep local state in sync if fee is modified externally
  useEffect(() => {
    if (existingCcFee) {
      setFeeKind(existingCcFee.kind);
      setFeeValue(existingCcFee.value);
      setFeeLabel(existingCcFee.label);
    }
  }, [existingCcFee]);

  // Sync to parent serviceFees state when toggled or values change
  const handleToggle = (checked: boolean) => {
    if (checked) {
      const val = feeValue.trim().length > 0 ? feeValue : DEFAULT_CC_RATE;
      if (!feeValue.trim()) {
        setFeeValue(DEFAULT_CC_RATE);
      }
      onServiceFeesChange((prev) => {
        const withoutCc = prev.filter((f) => f.id !== CC_FEE_ID && f.label.toLowerCase().trim() !== "credit card fee");
        return [
          ...withoutCc,
          {
            id: CC_FEE_ID,
            feeType: "credit_card_surcharge",
            label: feeLabel.trim() || "Credit card fee",
            kind: feeKind,
            value: val,
            appliesToType: "invoice",
            appliesToKey: "invoice",
          },
        ];
      });
    } else {
      onServiceFeesChange((prev) => prev.filter((f) => f.id !== CC_FEE_ID && f.label.toLowerCase().trim() !== "credit card fee"));
    }
  };

  const handleValueChange = (newVal: string) => {
    setFeeValue(newVal);
    if (!isEnabled) return;
    onServiceFeesChange((prev) =>
      prev.map((f) => (f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee" ? { ...f, value: newVal } : f)),
    );
  };

  const handleKindChange = (newKind: InvoiceAdjustmentKind) => {
    setFeeKind(newKind);
    if (!isEnabled) return;
    onServiceFeesChange((prev) =>
      prev.map((f) => (f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee" ? { ...f, kind: newKind } : f)),
    );
  };

  const handleLabelChange = (newLabel: string) => {
    setFeeLabel(newLabel);
    if (!isEnabled) return;
    onServiceFeesChange((prev) =>
      prev.map((f) => (f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee" ? { ...f, label: newLabel } : f)),
    );
  };

  const handleApplyPreset = (presetValue: string) => {
    setFeeKind("percentage");
    handleValueChange(presetValue);
    if (!isEnabled) {
      handleToggle(true);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "none",
        p: 2.5,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        bgcolor: "background.paper",
        transition: "opacity 0.2s ease",
        opacity: markAsPaid ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
          <CreditCardOutlinedIcon sx={{ color: colors.gray[500], mt: 0.25, fontSize: "1.25rem" }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.gray[700], lineHeight: 1.3 }}>
              Credit card fee
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", mt: 0.25 }}>
              Pass transaction fee to customer for online card payments
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={isEnabled && !markAsPaid}
          disabled={markAsPaid}
          onChange={(_, checked) => handleToggle(checked)}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          size="small"
        />
      </Box>

      <Collapse in={isEnabled && !markAsPaid} timeout={250}>
        <Stack spacing={2} sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.gray[200]}` }}>
          {/* Fee Input & Kind Selector */}
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={feeKind}
              onChange={(_, val) => {
                if (val) handleKindChange(val);
              }}
              sx={{
                height: 40,
                "& .MuiToggleButton-root": { px: 1.5, fontWeight: 700, fontSize: "0.8125rem" },
              }}
            >
              <ToggleButton value="percentage">%</ToggleButton>
              <ToggleButton value="amount">$</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              type="number"
              label={feeKind === "percentage" ? "Fee rate" : "Fee amount"}
              value={feeValue}
              onChange={(e) => handleValueChange(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              inputProps={{
                min: 0,
                step: "0.01",
                max: feeKind === "percentage" ? 100 : undefined,
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{feeKind === "percentage" ? "%" : "$"}</InputAdornment>,
              }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Preset Percentage Recommendations */}
          {feeKind === "percentage" && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                <Typography variant="caption" sx={{ color: colors.gray[600], fontWeight: 600, fontSize: "0.75rem" }}>
                  Quick select suggested rate:
                </Typography>
                <Typography variant="caption" sx={{ color: colors.gray[400], fontSize: "0.7rem" }}>
                  Typical: 2.9% – 3.5%
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {PRESET_PERCENTAGES.map((preset) => {
                  const isSelected = feeValue === preset.value || Number.parseFloat(feeValue) === Number.parseFloat(preset.value);
                  return (
                    <Tooltip key={preset.value} title={preset.tooltip} arrow placement="top">
                      <Chip
                        clickable
                        size="small"
                        onClick={() => handleApplyPreset(preset.value)}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography component="span" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                              {preset.label}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.6875rem",
                                opacity: isSelected ? 0.95 : 0.7,
                                fontWeight: 500,
                              }}
                            >
                              ({preset.tag})
                            </Typography>
                          </Box>
                        }
                        sx={{
                          height: 26,
                          borderRadius: "6px",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          border: isSelected ? "1.5px solid" : "1px solid",
                          borderColor: isSelected ? "primary.main" : colors.gray[200],
                          bgcolor: isSelected ? "primary.50" : "background.paper",
                          color: isSelected ? "primary.main" : colors.gray[700],
                          "&:hover": {
                            bgcolor: isSelected ? "primary.100" : colors.gray[50],
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Collapsible Rate Guide Helper */}
          <Box
            sx={{
              bgcolor: colors.blue[50],
              borderRadius: 1.5,
              border: `1px solid ${colors.blue[100]}`,
              p: 1.25,
            }}
          >
            <Box
              onClick={() => setShowRateGuide((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: colors.blue[600] }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: colors.blue[900], fontSize: "0.75rem" }}>
                  Estimated processor fees (Stripe & PayPal)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", color: colors.blue[600] }}>
                {showRateGuide ? <ExpandLessIcon sx={{ fontSize: "1.1rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1.1rem" }} />}
              </Box>
            </Box>

            <Collapse in={showRateGuide} timeout={200}>
              <Stack spacing={0.75} sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${colors.blue[200]}` }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ color: colors.blue[900], fontWeight: 500 }}>
                    • <strong>Stripe:</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.blue[800], fontWeight: 600 }}>
                    2.9% + $0.30 / transaction
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ color: colors.blue[900], fontWeight: 500 }}>
                    • <strong>PayPal (Credit Cards):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.blue[800], fontWeight: 600 }}>
                    2.99% + $0.49 / transaction
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ color: colors.blue[900], fontWeight: 500 }}>
                    • <strong>PayPal (Wallet / Invoicing):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.blue[800], fontWeight: 600 }}>
                    3.49% + $0.49 / transaction
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: colors.blue[800], fontSize: "0.7rem", mt: 0.5, fontStyle: "italic" }}>
                  Tip: A rate of <strong>3.0% to 3.5%</strong> covers both the percentage fee and the per-transaction cents for standard invoice amounts.
                </Typography>
              </Stack>
            </Collapse>
          </Box>

          <TextField
            size="small"
            label="Fee label on invoice"
            value={feeLabel}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder="Credit card fee"
            fullWidth
          />

          {computedFeeAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 1.25,
                py: 0.85,
                bgcolor: colors.gray[50],
                borderRadius: 1,
                border: `1px solid ${colors.gray[200]}`,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.gray[600], fontWeight: 600 }}>
                Estimated fee added to total:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.8125rem" }}>
                +{formatCurrency(computedFeeAmount)}
              </Typography>
            </Box>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}

